import React, { useEffect, useRef, useState } from 'react';

const AnimatedAuroraBackground = () => {
  const canvasRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  
  // Delay animation start to not block initial render
  useEffect(() => {
    const timer = setTimeout(() => setShouldAnimate(true), 100);
    return () => clearTimeout(timer);
  }, []);
  
  // Lazy load the background image with WebP support
  useEffect(() => {
    if (!shouldAnimate) return;
    
    // Check WebP support and load appropriate image
    const supportsWebP = document.createElement('canvas')
      .toDataURL('image/webp')
      .indexOf('data:image/webp') === 0;
    
    const img = new Image();
    img.src = supportsWebP ? '/aurora-bg.webp' : '/aurora-bg.jpg';
    img.onload = () => setImageLoaded(true);
  }, [shouldAnimate]);
  
  useEffect(() => {
    if (!shouldAnimate) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let particles = [];
    let lines = [];
    let lastTime = 0;
    const targetFPS = 30; // Limit to 30fps for performance
    const frameInterval = 1000 / targetFPS;
    
    // Set canvas size with device pixel ratio consideration
    const resizeCanvas = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      ctx.scale(dpr, dpr);
    };
    resizeCanvas();
    
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(resizeCanvas, 100);
    };
    window.addEventListener('resize', handleResize);
    
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    // Simplified Particle class - fewer calculations
    class Particle {
      constructor() {
        this.x = Math.random() * width;
        this.y = Math.random() * height * 0.7 + height * 0.1;
        this.size = Math.random() * 2 + 1.5;
        this.speedX = (Math.random() - 0.5) * 0.4;
        this.opacity = Math.random() * 0.4 + 0.3;
        this.trail = [];
        this.maxTrail = 30; // Reduced trail length
        this.hue = Math.random() > 0.5 ? 180 : 200;
      }
      
      update() {
        this.trail.push({ x: this.x, y: this.y });
        if (this.trail.length > this.maxTrail) this.trail.shift();
        
        this.x += this.speedX;
        if (this.x > width + 20) { this.x = -20; this.trail = []; }
        if (this.x < -20) { this.x = width + 20; this.trail = []; }
      }
      
      draw(ctx) {
        // Simple trail
        if (this.trail.length > 2) {
          ctx.beginPath();
          ctx.moveTo(this.trail[0].x, this.trail[0].y);
          for (let i = 1; i < this.trail.length; i++) {
            ctx.lineTo(this.trail[i].x, this.trail[i].y);
          }
          ctx.strokeStyle = `hsla(${this.hue}, 100%, 70%, ${this.opacity * 0.3})`;
          ctx.lineWidth = 1;
          ctx.stroke();
        }
        
        // Simple glow dot
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size * 2, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${this.hue}, 100%, 70%, ${this.opacity})`;
        ctx.fill();
      }
    }
    
    // Simplified flowing line
    class FlowingLine {
      constructor(yBase) {
        this.yBase = yBase;
        this.speed = Math.random() * 0.2 + 0.1;
        this.amp = Math.random() * 25 + 15;
        this.freq = Math.random() * 0.002 + 0.001;
        this.offset = Math.random() * 1000;
        this.opacity = Math.random() * 0.1 + 0.03;
      }
      
      draw(ctx, time) {
        ctx.beginPath();
        for (let x = 0; x <= width; x += 10) { // Larger step for performance
          const y = this.yBase + Math.sin(x * this.freq + time * 0.0005 * this.speed + this.offset) * this.amp;
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.strokeStyle = `rgba(0, 220, 255, ${this.opacity})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    }
    
    // Reduced particle count
    for (let i = 0; i < 15; i++) {
      particles.push(new Particle());
    }
    
    // Reduced line count
    for (let i = 0; i < 8; i++) {
      lines.push(new FlowingLine(height * 0.2 + height * 0.5 * (i / 8)));
    }
    
    // Throttled animation loop
    const animate = (currentTime) => {
      animationFrameId = requestAnimationFrame(animate);
      
      const elapsed = currentTime - lastTime;
      if (elapsed < frameInterval) return;
      lastTime = currentTime - (elapsed % frameInterval);
      
      ctx.clearRect(0, 0, width, height);
      
      // Draw lines
      lines.forEach(line => line.draw(ctx, currentTime));
      
      // Update and draw particles
      particles.forEach(p => {
        p.update();
        p.draw(ctx);
      });
    };
    
    animate(0);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
      clearTimeout(resizeTimeout);
    };
  }, [shouldAnimate]);
  
  return (
    <div className="fixed inset-0 z-0 overflow-hidden">
      {/* Base aurora image with WebP support and fade-in */}
      <picture>
        <source srcSet="/aurora-bg.webp" type="image/webp" />
        <div 
          className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          style={{
            backgroundImage: `url('/aurora-bg.jpg')`,
            filter: 'brightness(0.7)',
            willChange: 'transform'
          }}
        />
      </picture>
      
      {/* Fallback dark background while loading */}
      <div className={`absolute inset-0 bg-slate-950 transition-opacity duration-1000 ${imageLoaded ? 'opacity-0' : 'opacity-100'}`} />
      
      {/* Animated canvas */}
      <canvas 
        ref={canvasRef}
        className="absolute inset-0 pointer-events-none"
        style={{ mixBlendMode: 'screen', willChange: 'transform' }}
      />
      
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/30 via-transparent to-slate-950/50" />
    </div>
  );
};

export default AnimatedAuroraBackground;
